import React, { useMemo, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { toast } from "react-toastify";
import LoadingOverlay from "../common/LoadingOverlay";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const norm = (s) =>
    (s ?? "")
        .toString()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();

const DmeLogTable = ({
    items = [],           // todos los elementos (sin paginar)
    summary = { successCount: 0, errorCount: 0, naCount: 0 },
    loading,
    onDetail,
    // paginaci�n/b�squeda client-side
    page = 1,
    setPage = () => { },
    pageSize = 25,
    setPageSize = () => { },
    q = "",
    setQ = () => { },
    userEmail = ""       // opcional; si tu useApi no inyecta usuario, p�salo desde el padre
}) => {
    const { get, post } = useApi();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalReturnId, setModalReturnId] = useState(null);
    const [modalFolios, setModalFolios] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [loading2, setLoading2] = useState(false);
    const [exporting, setExporting] = useState(false);

    const fetchPendingFolios = async (returnId) => {
        setModalLoading(true);
        try {
            const res = await get("LimiteFoliosPendientes", { params: { returnId }, showError: false });
            setModalFolios(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Error fetching pending folios:", err);
            toast.error("No se pudo obtener folios pendientes.");
            setModalFolios([]);
        } finally {
            setModalLoading(false);
        }
    };

    const openModalFor = async (returnId) => {
        setModalReturnId(returnId);
        setModalOpen(true);
        await fetchPendingFolios(returnId);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalReturnId(null);
        setModalFolios([]);
    };

    const handleAuthorize = async (reference) => {
        if (!modalReturnId || !reference) return;
        if (!window.confirm(`Autorizar folio ${reference}?`)) return;

        setLoading2(true);
        try {
            await post("apruebaLimiteDME", {
                returnId: modalReturnId,
                reference,
                userEmail
            }, { showError: false });

            toast.success("Folio autorizado.");
            await fetchPendingFolios(modalReturnId);
            onDetail && onDetail(modalReturnId);
        } catch (err) {
            console.error("AuthorizeLimitFolio error:", err);
            const msg = err?.response?.data?.message || "Error autorizando folio";
            toast.error(msg);
        }
        finally {
            setLoading2(false);
        }
    };

    const handleReject = async (reference) => {
        if (!modalReturnId || !reference) return;
        if (!window.confirm(`Rechazar folio ${reference}?`)) return;
        setLoading2(true);
        try {
            await post("rechazaLimiteDME", {
                returnId: modalReturnId,
                reference,
                userEmail
            }, { showError: false });

            toast.success("Folio cancelado.");
            await fetchPendingFolios(modalReturnId);
            onDetail && onDetail(modalReturnId);
        } catch (err) {
            console.error("RejectLimitFolio error:", err);
            const msg = err?.response?.data?.message || "Error rechazando folio";
            toast.error(msg);
        }
        finally {
            setLoading2(false);
        }
    };

    const exportToExcel = () => {
        try {
            setExporting(true);
            // Exporta los registros filtrados; si quieres solo la p�gina usa `pageRows` en lugar de `filtered`
            const source = filtered; // o pageRows

            const data = source.map((r) => {
                const statusNum = r.status ?? r.Status ?? 0;
                const statusText =
                    statusNum === 0 ? "Carga completa" :
                        (statusNum === 1 || statusNum === 2 || statusNum === 3) ? "Pendientes por procesar" :
                            "Desconocido";

                return {
                    ID: r.returnId,
                    Cadena: r.chain,
                    Archivo: r.fileName,
                    Fecha: r.date ? new Date(r.date).toLocaleString() : "",
                    Estatus: statusText,
                    Cargado: r.loaded ?? r.Loaded ?? 0,
                    Error: r.error ?? r.Error ?? 0,
                    Pendiente: r.pending ?? r.Pending ?? 0,
                    Retenido: r.retained ?? r.Retained ?? 0,
                    Rechazado: r.rejected ?? r.Rejected ?? 0,
                    Duplicado: r.duplicate ?? r.Duplicate ?? 0
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "DME_Logs");

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            //const name = `dme_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.xlsx`;
            const pad = (n) => String(n).padStart(2, "0");
            const now = new Date();
            const name = `dme_logs_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}.xlsx`;
            saveAs(blob, name);
        } catch (err) {
            console.error("Export to Excel failed:", err);
            toast.error("No se pudo exportar a Excel.");
        } finally {
            setExporting(false);
        }
    };

    // b�squeda local (sobre todos los items)
    const filtered = useMemo(() => {
        const nq = norm(q);
        if (!nq) return items;
        return items.filter((row) =>
            [
                row.returnId,
                row.chain,
                row.fileName,
                row.date ? new Date(row.date).toLocaleString().replace(",", "") : "",
                // usamos status num�rico preferentemente; pero incluimos statusText/results por si acaso
                String(row.status ?? row.Status ?? ""),
                row.statusText ?? row.StatusText ?? "",
                row.results ?? row.Results ?? ""
            ]
                .map(norm)
                .some((v) => v.includes(nq))
        );
    }, [items, q]);

    // paginaci�n local
    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIdx = (page - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, total);
    const pageRows = filtered.slice(startIdx, endIdx);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    if (loading) {
        return <div className="text-center py-10 text-gray-500">Cargando registros...</div>;
    }

    if (!items.length) {
        return <div className="text-center py-10 text-gray-500">No se encontraron registros</div>;
    }

    return (
        <div className="bg-white rounded-xl shadow p-4">
            {/* resumen + buscador */}
            <div className="flex flex-wrap gap-4 mb-4">
                <div className="px-4 py-2 bg-green-100 text-green-800 rounded-xl text-sm">
                    Carga completa: {summary?.successCount ?? 0}
                </div>
                <div className="px-4 py-2 bg-red-100 text-red-800 rounded-xl text-sm">
                    Pendientes por procesar: {summary?.errorCount ?? 0}
                </div>
                <div className="ml-auto">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setPage(1); // reset a la p�gina 1 al buscar
                        }}
                        placeholder="Buscar (id, archivo, cadena, status, fecha)"
                        className="border rounded px-3 py-2 text-sm w-72"
                    />
                </div>
                <button
                    onClick={exportToExcel}
                    disabled={exporting}
                    className="bg-green-800 text-white px-3 py-2 rounded text-sm hover:bg-green-900 disabled:opacity-60"
                >
                    {exporting ? "Exportando..." : "Exportar a Excel"}
                </button>
            </div>

            {/* tabla */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead>
                        <tr className="border-b bg-gray-50 text-gray-600">
                            <th className="py-2 px-3">ID</th>
                            <th className="py-2 px-3">Cadena</th>
                            <th className="py-2 px-3">Archivo</th>
                            <th className="py-2 px-3">Fecha</th>
                            <th className="py-2 px-3">Estatus</th>

                            {/* Nuevas columnas de conteo por estatus */}
                            <th className="py-2 px-3 text-center">Por Clasificar</th>
                            <th className="py-2 px-3 text-center">Error</th>
                            <th className="py-2 px-3 text-center">Pendiente</th>
                            <th className="py-2 px-3 text-center">Retenido</th>
                            <th className="py-2 px-3 text-center">Cancelado</th>
                            <th className="py-2 px-3 text-center">Duplicado</th>

                            <th className="py-2 px-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row) => {
                            const statusNum = row.status ?? row.Status ?? 0;
                            const pendingCount = row.PendingLimitCount ?? row.pendingLimitCount ?? 0;

                            // Conteos por estatus (soportamos varias formas de nombre: camel/pascal)
                            const loaded = row.loaded ?? row.Loaded ?? 0;
                            const error = row.error ?? row.Error ?? 0;
                            const pending = row.pending ?? row.Pending ?? 0;
                            const retained = row.retained ?? row.Retained ?? 0;
                            const rejected = row.rejected ?? row.Rejected ?? 0;
                            const duplicate = row.duplicate ?? row.Duplicate ?? 0;

                            return (
                                <tr key={row.returnId} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-3">{row.returnId}</td>
                                    <td className="py-2 px-3">{row.chain}</td>
                                    <td className="py-2 px-3 break-all whitespace-nowrap">{row.fileName}</td>
                                    <td className="py-2 px-3">
                                        {row.date ? new Date(row.date).toLocaleString().replace(",", "") : "-"}
                                    </td>

                                    <td className="py-2 px-3">
                                        <span
                                            className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusNum === 0 ? "bg-green-100 text-green-700" :
                                                (statusNum === 1 || statusNum === 2 || statusNum === 3) ? "bg-red-100 text-red-700" :
                                                            "bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            {statusNum === 0
                                                ? "Carga completa"
                                                : (statusNum === 1 || statusNum === 2 || statusNum === 3)
                                                    ? "Pendientes por procesar"
                                                    : "Desconocido"}
                                        </span>
                                    </td>

                                    {/* Celdas de conteo (alineadas al centro y con peque�o badge) */}
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">
                                            {loaded}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs">
                                            {error}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs">
                                            {pending}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs">
                                            {retained}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs">
                                            {rejected}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs">
                                            {duplicate}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-right h-12 flex items-center justify-end gap-2">
                                        <button
                                            onClick={() => onDetail(row.returnId)}
                                            className="bg-blue-600 text-white px-3 py-0.5 rounded text-xs hover:bg-blue-700"
                                        >
                                            Detalle
                                        </button>
                                        {statusNum === 3 && (
                                            <button
                                                onClick={() => openModalFor(row.returnId)}
                                                className="bg-indigo-600 text-white px-3 py-0.5 rounded text-xs hover:bg-indigo-700"
                                                disabled={modalLoading}
                                            >
                                                Autorizar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {pageRows.length === 0 && (
                            <tr>
                                <td colSpan="14" className="py-6 text-center text-gray-500">
                                    Sin coincidencias en esta p&aacute;gina.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* footer de paginaci�n (igual que el detalle) */}
            {totalPages > 1 && (
                <div className="flex flex-wrap justify-between items-center gap-3 mt-4 border-t pt-3">
                    <div className="text-sm text-gray-600">
                        Mostrando{" "}
                        <span className="font-medium">{total ? startIdx + 1 : 0}</span> -{" "}
                        <span className="font-medium">{endIdx}</span> de{" "}
                        <span className="font-medium">{total}</span> registros
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm text-gray-600">Filas</label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="border rounded px-2 py-1 text-sm"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>

                        <button
                            disabled={page <= 1}
                            onClick={goPrev}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            -
                        </button>

                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">P&aacute;gina</span>
                            <select
                                value={page}
                                onChange={(e) => setPage(Number(e.target.value))}
                                className="border rounded px-2 py-1 text-sm"
                            >
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                            <span className="text-sm text-gray-600">de {totalPages}</span>
                        </div>

                        <button
                            disabled={page >= totalPages}
                            onClick={goNext}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                        >
                            +
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    {loading2 && <LoadingOverlay />}
                    <div className="bg-white rounded-lg w-full max-w-2xl p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-semibold">Autorizar l&iacute;mite - Carga #{modalReturnId}</h3>
                            <div className="flex gap-2">
                                <button className="px-3 py-1 rounded border" onClick={closeModal}>Cerrar</button>
                            </div>
                        </div>

                        {modalLoading ? (
                            <div>Cargando folios�</div>
                        ) : modalFolios.length === 0 ? (
                            <div>No hay folios pendientes (status = 3) para esta carga.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="p-2 text-left">Folio</th>
                                            <th className="p-2 text-right">Importe total</th>
                                            <th className="p-2 text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalFolios.map((f) => {
                                            const reference = f.reference ?? f.Reference;
                                            const totalAmount = f.totalAmount ?? f.TotalAmount ?? f.total ?? f.Total ?? 0;
                                            return (
                                                <tr key={reference} className="border-t">
                                                    <td className="p-2">{reference}</td>
                                                    <td className="p-2 text-right">
                                                        {Number(totalAmount).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2 text-right flex gap-2 justify-end">
                                                        <button
                                                            className="px-3 py-1 rounded bg-green-600 text-white text-xs"
                                                            onClick={() => handleAuthorize(reference)}
                                                        >
                                                            Autorizar
                                                        </button>
                                                        <button
                                                            className="px-3 py-1 rounded bg-red-600 text-white text-xs"
                                                            onClick={() => handleReject(reference)}
                                                        >
                                                            Rechazar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DmeLogTable;